import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Row, Col, Spinner } from 'react-bootstrap';
import SoftBadge from 'components/common/SoftBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faCreditCard, faUser, faTag, faKey } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import FalconCloseButton from 'components/common/FalconCloseButton';

const ModalDetalleSuscripcion = ({ show, onHide, idSuscripcion }) => {
    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();
    const [cargando, setCargando] = useState(true);
    const [info, setInfo] = useState(null);

    useEffect(() => {
        const fetchSuscripcion = async () => {
            if (!show || !idSuscripcion) return;
            setCargando(true);
            try {
                const response = await fetch(`/api/Suscripciones/ObtenerDetalleSuscripcion?idSuscripcion=${idSuscripcion}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.iResponseType === 1 && data.detalles) {
                        const parsed = JSON.parse(data.detalles);
                        const row = parsed.Table1 ? parsed.Table1[0] : (parsed.table1 ? parsed.table1[0] : null);
                        setInfo(row);
                    } else {
                        setInfo(null);
                    }
                } else {
                    if (response.status === 401) {
                        setToken(null);
                        window.location.href = '';
                    }
                }
            } catch (error) {
                console.error('Error al cargar datos de suscripción:', error);
                showErrorToast('Error al cargar la información de la suscripción');
            } finally {
                setCargando(false);
            }
        };

        fetchSuscripcion();
    }, [show, idSuscripcion]);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faArrowsRotate} className="me-2 text-info" />
                    Detalles de Suscripci&oacute;n #{idSuscripcion}
                </Modal.Title>
                <FalconCloseButton onClick={onHide} />
            </Modal.Header>
            <Modal.Body className="px-4 py-3">
                {cargando ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted fs--1">Cargando datos de la suscripci&oacute;n...</p>
                    </div>
                ) : !info ? (
                    <div className="text-center py-4 text-muted">
                        No se encontr&oacute; informaci&oacute;n detallada para esta suscripci&oacute;n.
                    </div>
                ) : (
                    <div>
                        <Row className="g-3 mb-3 border-bottom pb-3">
                            <Col md={6}>
                                <div className="fs--1 text-muted">ID Suscripci&oacute;n</div>
                                <div className="fw-bold fs-0">#{idSuscripcion}</div>
                            </Col>
                            <Col md={6} className="text-md-end">
                                <div className="fs--1 text-muted mb-1">Estatus</div>
                                <SoftBadge
                                    pill
                                    bg={info.vEstadoSuscripcion === 'ACTIVA' ? 'success' : 'danger'}
                                    className="fs--1 px-3 py-1"
                                >
                                    {info.vEstadoSuscripcion || 'INACTIVA'}
                                </SoftBadge>
                            </Col>
                        </Row>

                        <Row className="g-3">
                            <Col md={6}>
                                <div className="p-2 border rounded bg-light mb-2">
                                    <div className="fs--2 text-uppercase text-muted fw-bold mb-1">
                                        <FontAwesomeIcon icon={faUser} className="me-1" /> Cliente & Usuario
                                    </div>
                                    <div className="fs--1"><strong>ID Cliente:</strong> {info.idClienteAkasia || '-'}</div>
                                    <div className="fs--1"><strong>Creado por:</strong> {info.vCreatedUser || '-'}</div>
                                    <div className="fs--1">
                                        <strong>Fecha Creaci&oacute;n:</strong>{' '}
                                        {info.dCreatedDate ? new Date(info.dCreatedDate).toLocaleDateString('es-MX') : '-'}
                                    </div>
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className="p-2 border rounded bg-light mb-2">
                                    <div className="fs--2 text-uppercase text-muted fw-bold mb-1">
                                        <FontAwesomeIcon icon={faCreditCard} className="me-1" /> Plan y Pagos
                                    </div>
                                    <div className="fs--1"><strong>Tipo Renovaci&oacute;n:</strong> {info.vTipoRenovacion || '-'}</div>
                                    <div className="fs--1">
                                        <strong>Monto:</strong>{' '}
                                        ${parseFloat(info.dTotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} {info.vMoneda || 'MXN'}
                                    </div>
                                    <div className="fs--1">
                                        <strong>Pr&oacute;ximo Pago:</strong>{' '}
                                        {info.dProximoPago ? new Date(info.dProximoPago).toLocaleDateString('es-MX') : '-'}
                                    </div>
                                </div>
                            </Col>

                            {info.vStripeSubscriptionId && (
                                <Col md={12}>
                                    <div className="p-2 border rounded bg-light mb-2">
                                        <div className="fs--2 text-uppercase text-muted fw-bold mb-1">
                                            <FontAwesomeIcon icon={faTag} className="me-1" /> Identificador Stripe
                                        </div>
                                        <div className="fs--1 font-monospace">{info.vStripeSubscriptionId}</div>
                                    </div>
                                </Col>
                            )}

                            {info.idLicencia && (
                                <Col md={12}>
                                    <div className="p-2 border rounded bg-light mb-2">
                                        <div className="fs--2 text-uppercase text-muted fw-bold mb-1">
                                            <FontAwesomeIcon icon={faKey} className="me-1" /> Licencia Vinculada
                                        </div>
                                        <div className="fs--1"><strong>ID Licencia:</strong> #{info.idLicencia}</div>
                                        {info.vUUIDLicencia && <div className="fs--1 text-truncate"><strong>UUID Licencia:</strong> {info.vUUIDLicencia}</div>}
                                        {info.vUUIDEmpresa && <div className="fs--1 text-truncate"><strong>UUID Empresa:</strong> {info.vUUIDEmpresa}</div>}
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalDetalleSuscripcion.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    idSuscripcion: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ModalDetalleSuscripcion;
